import type { LegalPageSection } from './LegalPage';

/**
 * Hand-authored legal copy. Reviewed by Sean Foley (sole proprietor,
 * Maryland, USA). Edit-in-place — these strings ship straight into the
 * rendered DOM. Keep paragraphs short and plain-language.
 */

export const LEGAL_LAST_UPDATED = '12 May 2026';

export const TERMS_SECTIONS: readonly LegalPageSection[] = [
  {
    heading: 'what anomia is',
    bodyHtml: `
      <p>anomia is a browser-only tool that detects and removes personally identifiable information (PII) from documents and images. The entire process — file parsing, PII detection, redaction, and output generation — runs on your device. Your files never reach our servers because we don't operate any servers that handle user content.</p>
      <p>The service is provided as a website at <a href="https://anomia.app">anomia.app</a> and is delivered as static code your browser downloads and runs. There is no account system.</p>
    `,
  },
  {
    heading: 'pricing and license terms',
    bodyHtml: `
      <p>anomia offers a free tier and a paid Pro tier.</p>
      <ul>
        <li><strong>Free tier:</strong> up to 5 lifetime redactions, files up to 10 MB, batches up to 3 files. No account required.</li>
        <li><strong>Pro tier:</strong> US $19, one-time purchase. Unlocks unlimited redactions, files up to 100 MB, unlimited batches, drag-rectangle and text-highlight manual redactions, and the OCR pipeline for image-based documents. The license is perpetual for the version of anomia available at the time of purchase. Future major versions may require a new license.</li>
      </ul>
      <p>The Pro license is delivered as a digital license key via the email address you provide at checkout. Activating the license registers your browser as one of up to 5 devices linked to that key. You may use the license on up to 5 browsers / devices concurrently. Clearing browser storage on a device counts as a new device against the cap.</p>
      <p>Licenses are not transferable. Sharing or redistributing a license key is grounds for revocation without refund.</p>
    `,
  },
  {
    heading: 'refund policy',
    bodyHtml: `
      <p>We offer a <strong>30-day, no-questions-asked refund</strong> on Pro purchases. Email <a href="mailto:contact@anomia.app">contact@anomia.app</a> from the address you used at checkout, mention you'd like a refund, and we'll process it within a few business days. You don't need to explain why.</p>
      <p>After 30 days, refunds are at our discretion but rare. Chargebacks initiated without first contacting us may result in the license being revoked.</p>
    `,
  },
  {
    heading: 'acceptable use',
    bodyHtml: `
      <p>You may use anomia for any lawful purpose, personal or commercial. You may run the redacted output through any other tool, share it, archive it, etc. — the output file is yours.</p>
      <p>You may not:</p>
      <ul>
        <li>Resell, sublicense, or redistribute the anomia application or your Pro license key.</li>
        <li>Use anomia to redact documents you don't have the legal right to possess or modify.</li>
        <li>Attempt to circumvent the free-tier limits other than by purchasing Pro.</li>
        <li>Scrape, mirror, or rehost the anomia website with the intent of bypassing the license check.</li>
      </ul>
    `,
  },
  {
    heading: 'limitation of liability',
    bodyHtml: `
      <p>anomia is provided "as is," without warranty of any kind, express or implied. PII detection is a heuristic process. We work hard to catch as much as possible, but we cannot guarantee that every piece of personal information in your file will be detected and removed. <strong>You are responsible for reviewing the output before sharing it.</strong></p>
      <p>To the maximum extent permitted by law, Sean Foley (the sole proprietor operating anomia) is not liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of anomia, including but not limited to data loss, reputational harm, or downstream consequences of an undetected PII leak. Your sole remedy in the event of a dispute is a refund of the amount you paid for the Pro license, capped at US $19.</p>
    `,
  },
  {
    heading: 'changes to these terms',
    bodyHtml: `
      <p>We may update these Terms from time to time. Material changes will be announced via the homepage and via a notice posted on this page. Continued use of anomia after the effective date of an update constitutes acceptance of the new Terms. The "last updated" date at the top of this page is the canonical effective date.</p>
    `,
  },
  {
    heading: 'governing law',
    bodyHtml: `
      <p>These Terms are governed by the laws of the State of Maryland, United States, without regard to its conflict-of-laws provisions. Any disputes that cannot be resolved by direct communication will be resolved in the state or federal courts located in Maryland.</p>
    `,
  },
  {
    heading: 'contact',
    bodyHtml: `
      <p>Questions about these Terms: <a href="mailto:contact@anomia.app">contact@anomia.app</a>.</p>
      <p>Operator: Sean Foley, sole proprietor, Maryland, USA.</p>
    `,
  },
];

export const PRIVACY_SECTIONS: readonly LegalPageSection[] = [
  {
    heading: 'the short version',
    bodyHtml: `
      <p><strong>We don't collect or transmit the contents of your files. Ever.</strong> The whole point of anomia is to redact files before they leave your control. Sending them to us would defeat the purpose.</p>
      <p>You can verify this yourself: open your browser's DevTools, switch to the Network tab, drop a file into anomia, and watch. No request carrying your file goes out. The only network traffic is the initial page load and one-time downloads of the detection model from a CDN.</p>
    `,
  },
  {
    heading: 'what we do collect',
    bodyHtml: `
      <p><strong>Anonymous traffic analytics.</strong> We use Cloudflare's built-in analytics to count page views and see which pages are popular. Cloudflare collects this at the network level — no third-party JavaScript runs in your browser, no cookies are set for analytics. The data is aggregate (page-level counts) and does not include identifying information about you.</p>
      <p><strong>Payment information, via Stripe.</strong> If you buy a Pro license, Stripe processes your payment. Stripe receives your card details and email address. We receive your email address (for license delivery) and a Stripe-issued purchase reference. We never see or store your card number. Stripe's own privacy policy applies to their handling of your payment data: <a href="https://stripe.com/privacy">stripe.com/privacy</a>.</p>
      <p><strong>Email address, for license delivery.</strong> We receive the email you used at Stripe checkout. We send you exactly one email: your Pro license key. We do not add you to any mailing list. We retain the email indefinitely so we can re-send the license if you lose it or need to add a device.</p>
      <p><strong>Device IDs, for the per-license device cap.</strong> When you activate Pro on a browser, we generate a random identifier and send it to our license server along with your license key. The server tracks how many distinct device IDs have activated each license so we can enforce the 5-device cap. The device ID is random — it does not identify you personally and cannot be linked back to your IP or browser fingerprint.</p>
    `,
  },
  {
    heading: 'what we explicitly do not collect',
    bodyHtml: `
      <ul>
        <li><strong>The contents of your files.</strong> Not for processing, not for analytics, not for debugging, not ever.</li>
        <li><strong>Filenames.</strong> The output is always renamed to a generic <code>redacted.{ext}</code> — and even your input filename never reaches us.</li>
        <li><strong>PII detections.</strong> What anomia finds in your file stays in your browser.</li>
        <li><strong>Cookies for tracking.</strong> We don't set any. The only data we persist on your device is in <code>localStorage</code> and is functional only: your free-tier counter, your Pro license (if activated), and your device ID.</li>
        <li><strong>Browser fingerprints.</strong> We don't fingerprint your browser or device.</li>
        <li><strong>Third-party trackers.</strong> No Google Analytics, no Hotjar, no Mixpanel, no Sentry, no Intercom, no Facebook pixel. None.</li>
      </ul>
    `,
  },
  {
    heading: 'third parties',
    bodyHtml: `
      <ul>
        <li><strong>Cloudflare</strong> hosts the website and runs the license-activation server. Cloudflare sees the IP addresses of visitors at the network level. See: <a href="https://www.cloudflare.com/privacypolicy/">cloudflare.com/privacypolicy</a>.</li>
        <li><strong>Stripe</strong> processes Pro payments. See: <a href="https://stripe.com/privacy">stripe.com/privacy</a>.</li>
        <li><strong>Resend</strong> delivers the license-key email. They receive your email address and the license string. See: <a href="https://resend.com/legal/privacy-policy">resend.com/legal/privacy-policy</a>.</li>
        <li><strong>Hugging Face / Google Fonts CDNs</strong> serve the detection model weights and the website's typeface. These are static GET requests; no file contents accompany them.</li>
      </ul>
    `,
  },
  {
    heading: 'data retention',
    bodyHtml: `
      <p>Email addresses tied to a Pro license are retained for as long as the license is valid (perpetual). Device-activation records are retained for the life of the license. Stripe payment records are retained per Stripe's own retention rules.</p>
      <p>Aggregate analytics data is retained by Cloudflare per their default retention windows (typically under 30 days for raw data, longer for aggregates).</p>
    `,
  },
  {
    heading: 'your rights',
    bodyHtml: `
      <p>Even though we collect very little, you have rights under data-protection laws like the GDPR and CCPA. You can:</p>
      <ul>
        <li>Request a copy of the data we hold about you (typically just your purchase email and license records).</li>
        <li>Request that we delete your email from our records. This also deactivates your Pro license.</li>
        <li>Correct any inaccurate data.</li>
      </ul>
      <p>Email <a href="mailto:contact@anomia.app">contact@anomia.app</a> from the address tied to your purchase. We'll respond within 30 days.</p>
    `,
  },
  {
    heading: 'children',
    bodyHtml: `
      <p>anomia is not directed at children under 13. We do not knowingly collect any information from anyone under 13. If you believe a child has provided us with information, email us and we'll delete it.</p>
    `,
  },
  {
    heading: 'changes to this policy',
    bodyHtml: `
      <p>Material changes will be announced on the homepage and reflected in the "last updated" date at the top of this page. We will not retroactively reduce the privacy protections this policy describes.</p>
    `,
  },
  {
    heading: 'contact',
    bodyHtml: `
      <p>Privacy questions: <a href="mailto:contact@anomia.app">contact@anomia.app</a>.</p>
      <p>Operator: Sean Foley, sole proprietor, Maryland, USA.</p>
    `,
  },
];

export const REFUND_SECTIONS: readonly LegalPageSection[] = [
  {
    heading: '30 days, no questions asked',
    bodyHtml: `
      <p>If you bought a Pro license within the last 30 days and decide it's not for you, we'll refund you. You don't need to explain why. You don't need to prove you uninstalled anything. We don't try to talk you out of it.</p>
      <p>Email <a href="mailto:contact@anomia.app">contact@anomia.app</a> from the email address you used at Stripe checkout, mention that you'd like a refund, and we'll process it within a few business days. The refund goes back to the original payment method via Stripe.</p>
    `,
  },
  {
    heading: 'after 30 days',
    bodyHtml: `
      <p>After 30 days, refunds are at our discretion. We'll consider extenuating circumstances on a case-by-case basis, but the default answer is no.</p>
      <p>If anomia is materially broken for you — a detection is wrong, the app crashes on your documents, something fails consistently and we can't fix it — reach out before disputing the charge. We'd rather refund you than have a chargeback on file.</p>
    `,
  },
  {
    heading: 'chargebacks',
    bodyHtml: `
      <p>If you initiate a chargeback through your bank or card issuer without first contacting us, the associated license key will be revoked. We're not adversarial about refunds — there's no scenario where contacting us first is worse than going through your bank.</p>
    `,
  },
  {
    heading: 'free tier',
    bodyHtml: `
      <p>Free-tier usage costs nothing and there's nothing to refund. If you ran into the 5-lifetime-redaction cap and bought Pro to keep going, the 30-day refund window applies to that Pro purchase as normal.</p>
    `,
  },
  {
    heading: 'contact',
    bodyHtml: `
      <p>Refund requests: <a href="mailto:contact@anomia.app">contact@anomia.app</a>.</p>
      <p>Operator: Sean Foley, sole proprietor, Maryland, USA.</p>
    `,
  },
];
